import { useState, useEffect, useCallback } from 'react';

import { NavItem } from './nav-item';
import { navSectionClasses } from '../classes';
import { NavUl, NavLi, NavCollapse } from '../styles';

import type { NavListProps, NavSubListProps } from '../types';
import { usePathname } from 'next/navigation';
import { useActiveLink } from '../../routes/use-active-link';
import { isExternalLink } from '../../routes/utils';
import { pathForNextNavigation } from '@/lib/appBasePath';

// ----------------------------------------------------------------------

export function NavList({ data, render, depth, slotProps, enabledRootRedirect, siblingPaths }: NavListProps) {
  const pathname = usePathname();

  const checkIsActive = useCallback(
    (item: NavListProps['data']): boolean => {
      const normPath = pathForNextNavigation(pathname || '');
      const itemPath = pathForNextNavigation(item.path || '');

      if (itemPath && itemPath !== '/' && itemPath !== '#' && !itemPath.startsWith('http')) {
        const isMatch = item.children ? normPath.includes(itemPath) : (normPath === itemPath || normPath.startsWith(itemPath + '/'));
        if (isMatch) return true;
      }

      if (item.children) {
        return item.children.some((child) => checkIsActive(child));
      }

      return false;
    },
    [pathname]
  );

  const active = useActiveLink(data.path, !!data.children);
  const normPathname = pathForNextNavigation(pathname || '');
  const normItemPath = pathForNextNavigation(data.path || '');
  // A sibling nav item "claims" the current route more specifically than this
  // one when its path is a longer prefix of the pathname (e.g. Informed
  // Delivery's '/campaigns/informed-delivery' vs. Direct Mail's '/campaigns'
  // both prefix-match '/campaigns/informed-delivery/details') — in that case
  // this item shouldn't also render as active.
  const siblingMoreSpecificMatch = !data.children && (siblingPaths?.some((p) => {
    if (p === data.path) return false;
    const normP = pathForNextNavigation(p);
    if (!normP || normP === '/' || normP === '#' || normP.startsWith('http')) return false;
    const siblingMatches = normPathname === normP || normPathname.startsWith(normP + '/');
    return siblingMatches && normP.length > normItemPath.length;
  }) ?? false);
  const effectiveActive = active && !siblingMoreSpecificMatch;

  // Hover opens the accordion; only an explicit click closes it back — mouse
  // leave never closes it.
  const [openMode, setOpenMode] = useState<'closed' | 'open'>('closed');

  // isActiveRoute auto-opens the section containing the current route, but a
  // user click to collapse should always win over that — otherwise the active
  // section can never be closed. This tracks that explicit override.
  const [forceClosed, setForceClosed] = useState(false);

  const isActiveRoute = effectiveActive || checkIsActive(data);
  const openMenu = !forceClosed && (openMode === 'open' || isActiveRoute);

  useEffect(() => {
    if (!isActiveRoute) {
      setOpenMode('closed');
    }
    setForceClosed(false);
    // Only re-run when the route changes — not on every render where
    // isActiveRoute is recomputed, otherwise this would fight manual toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggleMenu = useCallback(() => {
    if (!data.children) return;
    if (openMenu) {
      setOpenMode('closed');
      setForceClosed(true);
    } else {
      setOpenMode('open');
      setForceClosed(false);
    }
  }, [data.children, openMenu]);

  const handleMouseEnterOpen = useCallback(() => {
    if (data.children) {
      setForceClosed(false);
      setOpenMode('open');
    }
  }, [data.children]);

  const effectivePath =
    enabledRootRedirect && !data.disableRootRedirect && data.children && data.path === '#'
      ? (data.children[0]?.path ?? data.path)
      : data.path;

  const renderNavItem = (
    <NavItem
      render={render}
      // slots
      path={effectivePath}
      icon={data.icon}
      info={data.info}
      title={data.title}
      caption={data.caption}
      // state
      depth={depth}
      active={effectiveActive}
      disabled={data.disabled}
      hasChild={!!data.children}
      open={data.children ? openMenu : undefined}
      externalLink={isExternalLink(data.path)}
      enabledRootRedirect={enabledRootRedirect && !data.disableRootRedirect}
      // styles
      slotProps={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
      // actions
      onToggleMenu={handleToggleMenu}
      onClick={data.onClick}
    />
  );

  // Hidden item by role
  if (data.roles && slotProps?.currentRole) {
    if (!data?.roles?.includes(slotProps?.currentRole)) {
      return null;
    }
  }

  // Has children
  if (data.children) {
    return (
      <NavLi
        disabled={data.disabled}
        onMouseEnter={handleMouseEnterOpen}
        sx={{
          [`& .${navSectionClasses.li}`]: {
            '&:first-of-type': { mt: 'var(--nav-item-gap)' },
          },
        }}
      >
        {renderNavItem}

        <NavCollapse
          data-group={data.title}
          in={openMenu}
          depth={depth}
          unmountOnExit
          mountOnEnter
          sx={{
            ml: '17px',
            mt: '2px',
            mb: depth === 1 ? '6px' : '2px',
            borderLeft: '1px solid var(--line)',
            [`& .${navSectionClasses.ul}`]: {
              position: 'relative',
              pl: 0,
              '&::before': { display: 'none' },
            },
          }}
        >
          <NavSubList
            data={data.children}
            render={render}
            depth={depth}
            slotProps={slotProps}
            enabledRootRedirect={enabledRootRedirect}
          />
        </NavCollapse>
      </NavLi>
    );
  }

  // Default
  return <NavLi disabled={data.disabled}>{renderNavItem}</NavLi>;
}

// ----------------------------------------------------------------------

function NavSubList({ data, render, depth, slotProps, enabledRootRedirect }: NavSubListProps) {
  const siblingPaths = data.map((item) => item.path);
  return (
    <NavUl sx={{ gap: 'var(--nav-item-gap)' }}>
      {data.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={depth + 1}
          slotProps={slotProps}
          enabledRootRedirect={enabledRootRedirect}
          siblingPaths={siblingPaths}
        />
      ))}
    </NavUl>
  );
}
